// Transport abstraction. WebSocket today; WebTransport (QUIC datagrams + a reliable
// stream) slots in behind the same interface for deployment.
//
// The lag simulator (?lag=ms&jitter=ms&loss=%) wraps any transport. "Unreliable" messages
// (inputs up, snapshots down) are dropped under loss, as datagrams would be; reliable ones
// (events, welcome, pongs) are delayed by a round trip instead, which is what TCP does.

export interface LagSim { lag: number; jitter: number; loss: number }

export interface Transport {
  send(bytes: Uint8Array, reliable: boolean): void;
  onMessage: (data: ArrayBuffer) => void;
  onOpen: () => void;
  onClose: (reason: string) => void;
  close(): void;
  readonly bytesIn: number;
  readonly bytesOut: number;
}

export class WsTransport implements Transport {
  onMessage: (data: ArrayBuffer) => void = () => {};
  onOpen: () => void = () => {};
  onClose: (reason: string) => void = () => {};
  bytesIn = 0;
  bytesOut = 0;
  private ws: WebSocket;
  private sim: LagSim;
  private lastOut = 0;
  private lastIn = 0;

  constructor(url: string, sim: LagSim) {
    this.sim = sim;
    this.ws = new WebSocket(url);
    this.ws.binaryType = 'arraybuffer';
    this.ws.onopen = () => this.onOpen();
    this.ws.onclose = (e) => this.onClose(e.reason || `closed (${e.code})`);
    this.ws.onerror = () => {};
    this.ws.onmessage = (e) => {
      const data = e.data as ArrayBuffer;
      this.bytesIn += data.byteLength;
      if (!this.sim.lag && !this.sim.loss && !this.sim.jitter) { this.onMessage(data); return; }
      // first byte 2 = snapshot: unreliable
      const reliable = new Uint8Array(data)[0] !== 2;
      this.delay(reliable, true, () => this.onMessage(data));
    };
  }

  send(bytes: Uint8Array, reliable: boolean) {
    if (this.ws.readyState !== WebSocket.OPEN) return;
    this.bytesOut += bytes.byteLength;
    if (!this.sim.lag && !this.sim.loss && !this.sim.jitter) { this.ws.send(bytes); return; }
    const copy = bytes.slice();
    this.delay(reliable, false, () => { if (this.ws.readyState === WebSocket.OPEN) this.ws.send(copy); });
  }

  private delay(reliable: boolean, incoming: boolean, fn: () => void) {
    const s = this.sim;
    const lost = Math.random() * 100 < s.loss;
    if (lost && !reliable) return;
    let d = s.lag / 2 + Math.random() * s.jitter;
    if (lost) d += s.lag; // a retransmit costs a round trip
    const now = performance.now();
    let at = now + d;
    if (reliable) {
      // reliable streams stay in order
      if (incoming) { at = Math.max(at, this.lastIn); this.lastIn = at; }
      else { at = Math.max(at, this.lastOut); this.lastOut = at; }
    }
    setTimeout(fn, at - now);
  }

  close() { this.ws.close(); }
}
